/**
 * ApprovalStatusBanner Component
 *
 * Displays guide's current approval status with visual indicator and action buttons.
 * Shows urgent requirements count and provides link to requirements checklist.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-150 - Guide Dashboard Enhancements (13 SP)
 *
 * Props:
 * - status: object with approval_status, pending_requirements_count, urgent_requirements_count
 * - onViewRequirements: function to navigate to requirements view
 * - loading: boolean
 */

import {
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  FileText,
  ArrowRight
} from 'lucide-react';

/**
 * Get status configuration (color, icon, message)
 */
function getStatusConfig(approvalStatus) {
  const configs = {
    approved: {
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      icon: CheckCircle,
      iconColor: 'text-green-600',
      title: 'Profile Approved',
      message: 'Your guide profile is approved and active. You can now receive bookings.'
    },
    pending: {
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
      icon: Clock,
      iconColor: 'text-yellow-600',
      title: 'Approval Pending',
      message: 'Your profile is under review. We\'ll notify you once approved.'
    },
    pending_information: {
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      icon: FileText,
      iconColor: 'text-blue-600',
      title: 'Additional Information Required',
      message: 'Please complete the requirements below to proceed with approval.'
    },
    pending_verification: {
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-800',
      icon: Clock,
      iconColor: 'text-purple-600',
      title: 'Verification in Progress',
      message: 'We\'re verifying your submitted information.'
    },
    pending_licensing: {
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-800',
      icon: AlertTriangle,
      iconColor: 'text-orange-600',
      title: 'Licensing Requirements Pending',
      message: 'Please complete licensing requirements to continue.'
    },
    pending_insurance: {
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-800',
      icon: AlertTriangle,
      iconColor: 'text-orange-600',
      title: 'Insurance Documentation Pending',
      message: 'Please provide insurance documentation to continue.'
    },
    pending_training: {
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      icon: FileText,
      iconColor: 'text-blue-600',
      title: 'Training Certification Pending',
      message: 'Please complete required training certifications.'
    },
    conditionally_approved: {
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      icon: CheckCircle,
      iconColor: 'text-green-600',
      title: 'Conditionally Approved',
      message: 'Your profile is approved with conditions. Complete remaining requirements for full approval.'
    },
    rejected: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      icon: XCircle,
      iconColor: 'text-red-600',
      title: 'Application Rejected',
      message: 'Your application was not approved. Please review the feedback and reapply.'
    }
  };

  return configs[approvalStatus] || configs.pending;
}

export default function ApprovalStatusBanner({ status, onViewRequirements, loading = false }) {
  if (loading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
      </div>
    );
  }

  if (!status) {
    return null;
  }

  const config = getStatusConfig(status.approval_status);
  const Icon = config.icon;
  const hasPendingRequirements = status.pending_requirements_count > 0;
  const hasUrgentRequirements = status.urgent_requirements_count > 0;

  // Don't show banner if approved and no pending requirements
  if (status.approval_status === 'approved' && !hasPendingRequirements) {
    return null;
  }

  // Signup completion screen already shows "Your application is under review"
  if (status.approval_status === 'pending') {
    return null;
  }

  return (
    <div className={`${config.bgColor} border ${config.borderColor} rounded-lg p-6`}>
      <div className="flex items-start justify-between">
        {/* Left side - Status info */}
        <div className="flex items-start gap-4 flex-1">
          <div className={`p-3 bg-white rounded-lg ${config.borderColor} border`}>
            <Icon className={`w-6 h-6 ${config.iconColor}`} />
          </div>
          <div className="flex-1">
            <h3 className={`text-lg font-semibold ${config.textColor} mb-1`}>
              {config.title}
            </h3>
            <p className={`text-sm ${config.textColor} mb-3`}>
              {config.message}
            </p>

            {/* Requirements summary */}
            {hasPendingRequirements && (
              <div className="flex items-center gap-4 text-sm">
                {status.pending_requirements_count > 0 && (
                  <div className="flex items-center gap-2">
                    <FileText className={`w-4 h-4 ${config.iconColor}`} />
                    <span className={config.textColor}>
                      <span className="font-semibold">{status.pending_requirements_count}</span> pending requirement{status.pending_requirements_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
                {hasUrgentRequirements && (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                    <span className="text-red-800 font-semibold">
                      {status.urgent_requirements_count} urgent
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Next due date */}
            {status.next_due_date && (
              <div className="mt-2 text-xs text-gray-600">
                Next due: {new Date(status.next_due_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right side - Action button */}
        {hasPendingRequirements && onViewRequirements && (
          <button
            onClick={onViewRequirements}
            className={`flex items-center gap-2 px-4 py-2 ${config.bgColor === 'bg-green-50' ? 'bg-green-600' : 'bg-white'} ${config.bgColor === 'bg-green-50' ? 'text-white' : config.textColor} border ${config.borderColor} rounded-lg hover:opacity-90 transition-opacity font-medium text-sm whitespace-nowrap`}
          >
            View Requirements
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
